import { toAscii, tokenize } from "./tokenizer";
import { ExtractedEntities, UserDictionaryEntry } from "../types";
import { getDictionariesForIndustry, recordMapping, recordUnknown } from "./dictionary_service";
import { getDictionaryMappings } from "../offline_storage";

const FIELD_MAP: Record<string, keyof ExtractedEntities> = {
  equipment: "equipment",
  failure: "failure",
  safety: "failure",
  process: "topic",
  material: "topic",
  slang: "topic"
};

// Częste polskie słowa nieznaczące dla słownika branżowego — pomijane przy zbieraniu
// "nierozpoznanych" tokenów (żeby nie zaśmiecać logu treningowego).
const STOPWORDS = new Set([
  "jest", "bardzo", "proszę", "prosze", "trzeba", "żeby", "zeby", "który", "ktory",
  "która", "ktora", "które", "ktore", "tutaj", "teraz", "dzisiaj", "wczoraj",
  "zostać", "zostac", "będzie", "bedzie", "został", "zostal", "została", "zostala",
  "jakiś", "jakis", "jakaś", "jakas", "gdzie", "kiedy", "dlatego", "ponieważ",
  "poniewaz", "jeszcze", "znowu", "już", "juz", "można", "mozna", "trzeba",
]);

function normalizeMatch(text: string): string {
  return toAscii(text).toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

// Boost wagi na podstawie liczby wcześniejszych, potwierdzonych dopasowań tego samego
// rawToken → canonical (self-learning: częściej używane/potwierdzane mapowania wygrywają
// przy niejednoznacznych dopasowaniach).
async function buildOccurrenceBoost(): Promise<Map<string, number>> {
  const mappings = await getDictionaryMappings();
  const boost = new Map<string, number>();
  for (const m of mappings) {
    const key = `${normalizeMatch(m.rawToken)}::${normalizeMatch(m.resolvedCanonical)}`;
    boost.set(key, 1 + Math.min(m.occurrenceCount, 20) * 0.02);
  }
  return boost;
}

export async function matchDictionaries(
  transcript: string,
  industry: string,
  plantId?: string,
  managerId?: number
): Promise<ExtractedEntities> {
  const normText = normalizeMatch(transcript);
  const entries = await getDictionariesForIndustry(industry, plantId, managerId);
  const occurrenceBoost = await buildOccurrenceBoost();
  const sorted = [...entries].sort((a, b) => normalizeMatch(b.term).length - normalizeMatch(a.term).length);
  const canonicalCategory = new Map<string, string>();

  for (const e of entries) {
    if (e.category !== "slang") canonicalCategory.set(e.canonical, e.category);
  }

  const best: Record<string, { value: string; score: number }> = {};
  const matchedTerms: string[] = [];

  for (const e of sorted) {
    const normTerm = normalizeMatch(e.term);
    if (!normText.includes(normTerm)) continue;

    const targetCanonical = e.category === "slang" ? e.canonical : e.term;
    const targetCategory = e.category === "slang"
      ? (canonicalCategory.get(e.canonical) ?? "slang")
      : e.category;
    const field = FIELD_MAP[targetCategory];
    if (!field) continue;

    const boostKey = `${normTerm}::${normalizeMatch(targetCanonical)}`;
    const boost = occurrenceBoost.get(boostKey) ?? 1;
    const score = normTerm.length * e.weight * boost;
    if (best[field] && score <= best[field].score) continue;

    best[field] = { value: targetCanonical, score };
    matchedTerms.push(normTerm);

    await recordMapping(
      e.term,
      targetCanonical,
      targetCategory as UserDictionaryEntry["category"],
      industry,
      plantId || industry,
      managerId
    );
  }

  // Zbieranie nierozpoznanych, potencjalnie znaczących słów (do log treningowego —
  // po synchronizacji można je wykorzystać do rozbudowy słownika zakładowego).
  const covered = new Set<string>();
  for (const term of matchedTerms) for (const w of term.split(" ")) covered.add(w);
  const unresolved = tokenize(transcript).filter(w =>
    w.length >= 4 && !STOPWORDS.has(w) && !covered.has(toAscii(w))
  );
  if (unresolved.length > 0) {
    await recordUnknown(transcript, [...new Set(unresolved)], industry, plantId || industry, managerId);
  }

  return {
    equipment: best.equipment?.value,
    failure: best.failure?.value,
    topic: best.topic?.value
  };
}
